import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type EntityStatus = 'DRAFT' | 'SAVED' | 'PUBLISHED';
export type ActiveLevel = 'campaign' | 'adgroup' | 'ad';

export interface WorkspaceAd {
  id: string;
  name: string;
  status: EntityStatus;
  formData: Record<string, unknown>;
  isLocked: boolean;
  apiId?: string;
}

export interface WorkspaceAdGroup {
  id: string;
  name: string;
  status: EntityStatus;
  formData: Record<string, unknown>;
  isLocked: boolean;
  apiId?: string;
  ads: WorkspaceAd[];
}

export interface WorkspaceCampaign {
  id: string;
  name: string;
  objective: string;
  status: EntityStatus;
  formData: Record<string, unknown>;
  isLocked: boolean;
  apiId?: string;
}

export interface WorkspaceState {
  draftId: string;
  activeLevel: ActiveLevel;
  activeAdGroupId: string | null;
  activeAdId: string | null;
  campaign: WorkspaceCampaign;
  adGroups: WorkspaceAdGroup[];
  isSubmitting: boolean;
}

interface ContextValue extends WorkspaceState {
  setActiveLevel: (level: ActiveLevel, adGroupId?: string | null, adId?: string | null) => void;

  updateCampaignForm: (partial: Partial<WorkspaceCampaign['formData']>) => void;
  lockCampaign: (apiId: string) => void;
  unlockCampaign: () => void;

  addAdGroup: () => string;
  updateAdGroupForm: (id: string, partial: Partial<WorkspaceAdGroup['formData']>) => void;
  lockAdGroup: (id: string, apiId: string) => void;
  unlockAdGroup: (id: string) => void;
  deleteAdGroup: (id: string) => void;
  duplicateAdGroup: (id: string) => string;

  addAd: (adGroupId: string) => string;
  updateAdForm: (adGroupId: string, adId: string, partial: Partial<WorkspaceAd['formData']>) => void;
  lockAd: (adGroupId: string, adId: string, apiId: string) => void;
  unlockAd: (adGroupId: string, adId: string) => void;
  deleteAd: (adGroupId: string, adId: string) => void;
  duplicateAd: (adGroupId: string, adId: string) => string;

  setIsSubmitting: (v: boolean) => void;
  saveDraft: () => void;
  discardDraft: () => void;
  reset: () => void;

  activeAdGroup: WorkspaceAdGroup | null;
  activeAd: WorkspaceAd | null;
  canPublish: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const DRAFT_KEY = 'slightercrm.tt-workspace-draft';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function defaultCampaign(): WorkspaceCampaign {
  return { id: genId(), name: '', objective: '', status: 'DRAFT', formData: {}, isLocked: false };
}

function defaultAdGroup(name = 'Ad Group 1'): WorkspaceAdGroup {
  return { id: genId(), name, status: 'DRAFT', formData: {}, isLocked: false, ads: [] };
}

function defaultAd(name = 'Ad 1'): WorkspaceAd {
  return { id: genId(), name, status: 'DRAFT', formData: {}, isLocked: false };
}

function defaultState(): WorkspaceState {
  return {
    draftId: genId(),
    activeLevel: 'campaign',
    activeAdGroupId: null,
    activeAdId: null,
    campaign: defaultCampaign(),
    adGroups: [],
    isSubmitting: false,
  };
}

interface PersistedDraft { savedAt: number; state: WorkspaceState }

function loadDraft(): { state: WorkspaceState; savedAt: number } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDraft;
    if (parsed.savedAt && parsed.state) {
      if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) return null;
      return { state: parsed.state, savedAt: parsed.savedAt };
    }
    return null;
  } catch {
    return null;
  }
}

function isMeaningfulDraft(s: WorkspaceState): boolean {
  if (s.campaign.name) return true;
  if (s.campaign.objective) return true;
  if (s.adGroups.length > 0) return true;
  if (Object.keys(s.campaign.formData || {}).length > 0) return true;
  return false;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const Ctx = createContext<ContextValue | null>(null);

export function TiktokWorkspaceProvider({
  children,
  resumeDraftId,
}: { children: React.ReactNode; resumeDraftId?: string }) {
  const [state, setState] = useState<WorkspaceState>(() => {
    const saved = loadDraft();
    if (resumeDraftId) {
      if (saved && saved.state.draftId === resumeDraftId) return saved.state;
      return defaultState();
    }
    if (saved && isMeaningfulDraft(saved.state)) return saved.state;
    return defaultState();
  });

  // Auto-save (debounced 1.5s).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const env: PersistedDraft = { savedAt: Date.now(), state };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(env));
      } catch { /* quota */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [state]);

  const setActiveLevel = useCallback((level: ActiveLevel, adGroupId?: string | null, adId?: string | null) => {
    setState(prev => ({
      ...prev,
      activeLevel: level,
      activeAdGroupId: adGroupId !== undefined ? adGroupId : prev.activeAdGroupId,
      activeAdId: adId ?? null,
    }));
  }, []);

  const updateCampaignForm = useCallback((partial: Partial<WorkspaceCampaign['formData']>) => {
    setState(prev => {
      if (prev.campaign.isLocked) return prev;
      const fd = { ...prev.campaign.formData, ...partial } as Record<string, unknown>;
      const name = (fd.campaignName as string) || (fd.name as string) || prev.campaign.name;
      const objective = (fd.objectiveType as string) || (fd.objective as string) || prev.campaign.objective;
      return { ...prev, campaign: { ...prev.campaign, formData: fd, name, objective } };
    });
  }, []);

  const lockCampaign = useCallback((apiId: string) => {
    setState(prev => ({
      ...prev,
      campaign: { ...prev.campaign, isLocked: true, status: 'SAVED', apiId },
    }));
  }, []);

  const unlockCampaign = useCallback(() => {
    setState(prev => ({ ...prev, campaign: { ...prev.campaign, isLocked: false } }));
  }, []);

  const addAdGroup = useCallback((): string => {
    let newId = '';
    setState(prev => {
      const ag = defaultAdGroup(`Ad Group ${prev.adGroups.length + 1}`);
      newId = ag.id;
      return {
        ...prev,
        adGroups: [...prev.adGroups, ag],
        activeLevel: 'adgroup',
        activeAdGroupId: ag.id,
        activeAdId: null,
      };
    });
    return newId;
  }, []);

  const updateAdGroupForm = useCallback((id: string, partial: Partial<WorkspaceAdGroup['formData']>) => {
    setState(prev => ({
      ...prev,
      adGroups: prev.adGroups.map(ag => {
        if (ag.id !== id || ag.isLocked) return ag;
        const fd = { ...ag.formData, ...partial } as Record<string, unknown>;
        const name = (fd.adGroupName as string) || (fd.name as string) || ag.name;
        return { ...ag, formData: fd, name };
      }),
    }));
  }, []);

  const lockAdGroup = useCallback((id: string, apiId: string) => {
    setState(prev => ({
      ...prev,
      adGroups: prev.adGroups.map(ag =>
        ag.id === id ? { ...ag, isLocked: true, status: 'SAVED' as EntityStatus, apiId } : ag,
      ),
    }));
  }, []);

  const unlockAdGroup = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      adGroups: prev.adGroups.map(ag => ag.id === id ? { ...ag, isLocked: false } : ag),
    }));
  }, []);

  const deleteAdGroup = useCallback((id: string) => {
    setState(prev => {
      const ag = prev.adGroups.find(x => x.id === id);
      if (ag?.isLocked) return prev;
      const wasActive = prev.activeAdGroupId === id;
      return {
        ...prev,
        adGroups: prev.adGroups.filter(x => x.id !== id),
        activeLevel: wasActive ? 'campaign' : prev.activeLevel,
        activeAdGroupId: wasActive ? null : prev.activeAdGroupId,
        activeAdId: wasActive ? null : prev.activeAdId,
      };
    });
  }, []);

  const duplicateAdGroup = useCallback((id: string): string => {
    let newId = '';
    setState(prev => {
      const orig = prev.adGroups.find(x => x.id === id);
      if (!orig) return prev;
      const dup: WorkspaceAdGroup = {
        ...orig,
        id: genId(),
        name: `${orig.name} (Copy)`,
        status: 'DRAFT',
        isLocked: false,
        apiId: undefined,
        ads: orig.ads.map(ad => ({
          ...ad,
          id: genId(),
          name: `${ad.name} (Copy)`,
          status: 'DRAFT' as EntityStatus,
          isLocked: false,
          apiId: undefined,
        })),
      };
      newId = dup.id;
      return { ...prev, adGroups: [...prev.adGroups, dup], activeAdGroupId: dup.id, activeLevel: 'adgroup', activeAdId: null };
    });
    return newId;
  }, []);

  const addAd = useCallback((adGroupId: string): string => {
    let newId = '';
    setState(prev => {
      const ag = prev.adGroups.find(x => x.id === adGroupId);
      if (!ag) return prev;
      const ad = defaultAd(`Ad ${ag.ads.length + 1}`);
      newId = ad.id;
      return {
        ...prev,
        adGroups: prev.adGroups.map(x => x.id === adGroupId ? { ...x, ads: [...x.ads, ad] } : x),
        activeLevel: 'ad',
        activeAdGroupId: adGroupId,
        activeAdId: ad.id,
      };
    });
    return newId;
  }, []);

  const updateAdForm = useCallback((adGroupId: string, adId: string, partial: Partial<WorkspaceAd['formData']>) => {
    setState(prev => ({
      ...prev,
      adGroups: prev.adGroups.map(ag =>
        ag.id !== adGroupId ? ag : {
          ...ag,
          ads: ag.ads.map(ad => {
            if (ad.id !== adId || ad.isLocked) return ad;
            const fd = { ...ad.formData, ...partial } as Record<string, unknown>;
            const name = (fd.adName as string) || (fd.name as string) || ad.name;
            return { ...ad, formData: fd, name };
          }),
        },
      ),
    }));
  }, []);

  const lockAd = useCallback((adGroupId: string, adId: string, apiId: string) => {
    setState(prev => ({
      ...prev,
      adGroups: prev.adGroups.map(ag =>
        ag.id !== adGroupId ? ag : {
          ...ag,
          ads: ag.ads.map(ad =>
            ad.id === adId ? { ...ad, isLocked: true, status: 'SAVED' as EntityStatus, apiId } : ad,
          ),
        },
      ),
    }));
  }, []);

  const unlockAd = useCallback((adGroupId: string, adId: string) => {
    setState(prev => ({
      ...prev,
      adGroups: prev.adGroups.map(ag =>
        ag.id !== adGroupId ? ag : {
          ...ag,
          ads: ag.ads.map(ad => ad.id === adId ? { ...ad, isLocked: false } : ad),
        },
      ),
    }));
  }, []);

  const deleteAd = useCallback((adGroupId: string, adId: string) => {
    setState(prev => {
      const ag = prev.adGroups.find(x => x.id === adGroupId);
      const ad = ag?.ads.find(x => x.id === adId);
      if (ad?.isLocked) return prev;
      const wasActive = prev.activeAdId === adId;
      return {
        ...prev,
        adGroups: prev.adGroups.map(x =>
          x.id !== adGroupId ? x : { ...x, ads: x.ads.filter(a => a.id !== adId) },
        ),
        activeLevel: wasActive ? 'adgroup' : prev.activeLevel,
        activeAdId: wasActive ? null : prev.activeAdId,
      };
    });
  }, []);

  const duplicateAd = useCallback((adGroupId: string, adId: string): string => {
    let newId = '';
    setState(prev => {
      const ag = prev.adGroups.find(x => x.id === adGroupId);
      const orig = ag?.ads.find(x => x.id === adId);
      if (!orig) return prev;
      const dup: WorkspaceAd = {
        ...orig,
        id: genId(),
        name: `${orig.name} (Copy)`,
        status: 'DRAFT',
        isLocked: false,
        apiId: undefined,
      };
      newId = dup.id;
      return {
        ...prev,
        adGroups: prev.adGroups.map(x =>
          x.id !== adGroupId ? x : { ...x, ads: [...x.ads, dup] },
        ),
        activeAdId: dup.id,
        activeLevel: 'ad',
      };
    });
    return newId;
  }, []);

  const setIsSubmitting = useCallback((v: boolean) => {
    setState(prev => ({ ...prev, isSubmitting: v }));
  }, []);

  const saveDraft = useCallback(() => {
    try {
      const env: PersistedDraft = { savedAt: Date.now(), state };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(env));
    } catch { /* ignore */ }
  }, [state]);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setState(defaultState());
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setState(defaultState());
  }, []);

  const activeAdGroup = useMemo(
    () => state.adGroups.find(x => x.id === state.activeAdGroupId) ?? null,
    [state.adGroups, state.activeAdGroupId],
  );

  const activeAd = useMemo(
    () => (!activeAdGroup || !state.activeAdId ? null : activeAdGroup.ads.find(x => x.id === state.activeAdId) ?? null),
    [activeAdGroup, state.activeAdId],
  );

  const canPublish = useMemo(() => {
    if (!state.campaign.isLocked) return false;
    const lockedGroup = state.adGroups.find(x => x.isLocked);
    if (!lockedGroup) return false;
    return !!lockedGroup.ads.find(x => x.isLocked);
  }, [state.campaign.isLocked, state.adGroups]);

  const value: ContextValue = {
    ...state,
    setActiveLevel,
    updateCampaignForm,
    lockCampaign,
    unlockCampaign,
    addAdGroup,
    updateAdGroupForm,
    lockAdGroup,
    unlockAdGroup,
    deleteAdGroup,
    duplicateAdGroup,
    addAd,
    updateAdForm,
    lockAd,
    unlockAd,
    deleteAd,
    duplicateAd,
    setIsSubmitting,
    saveDraft,
    discardDraft,
    reset,
    activeAdGroup,
    activeAd,
    canPublish,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTiktokWorkspace(): ContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTiktokWorkspace must be used inside TiktokWorkspaceProvider');
  return ctx;
}
